import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import s from '../../pages/PedidosOrcamentos.module.css';

const fmt  = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const num  = v => parseFloat(String(v).replace(',', '.')) || 0;
// Valor em reais digitado: "1.234,56", "150,5" ou "150.50"
const numReais = v => {
  const t = String(v ?? '').trim();
  return parseFloat(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t) || 0;
};

/**
 * Simulação de Preços da NOTA DE COMPRA (botão "💲 Atualizar Preços" da
 * Entrada de NF). Grade com todos os produtos da nota: custo de chegada
 * (unitário + IPI + ST + rateio de frete/seguro/desconto), preço atual,
 * margem ↔ preço bidirecionais conforme o método da organização:
 *  - markup comum:   preço = custo × (1 + margem%)
 *  - markup divisor: preço = custo ÷ (1 − (Σíndices do TMF + margem%)/100)
 * No divisor, cada item tem o botão 🧮 que abre a Formação de Preço detalhada
 * (estilo do sistema antigo). "Gravar preços" atualiza o CADASTRO dos produtos.
 */
export default function ModalSimulacaoPrecos({ entradaId, onClose, onGravado }) {
  const [dados, setDados]     = useState(null);
  const [linhas, setLinhas]   = useState([]);
  const [erro, setErro]       = useState('');
  const [salvando, setSalvando] = useState(false);
  const [atualizarCusto, setAtualizarCusto] = useState(true);
  const [detalheIdx, setDetalheIdx] = useState(null); // janela de formação (divisor)
  const [unidades, setUnidades] = useState([]); // cadastro de unidades — combobox de "Unid. Cadastro"
  const [reconciliando, setReconciliando] = useState(false);
  const [resultReconciliar, setResultReconciliar] = useState(null);
  // Frete pago por fora da nota (CT-e), rateado nos itens por valor ou peso
  const [frete, setFrete] = useState({ valor: '', rateio: 'valor' });
  const [aplicandoFrete, setAplicandoFrete] = useState(false);
  // Navegação estilo planilha: Enter/↓ vai p/ o MESMO campo do item de baixo,
  // ↑ volta — pulando itens sem produto vinculado (inputs desabilitados)
  const campoRefs = React.useRef({}); // { 'margem-0': el, 'preco-0': el, ... }
  const focarProximo = (campo, idx, direcao) => {
    for (let i = idx + direcao; i >= 0 && i < linhas.length; i += direcao) {
      const el = campoRefs.current[campo + '-' + i];
      if (el && !el.disabled) { el.focus(); el.select(); return; }
    }
  };
  const navegacao = (campo, idx) => e => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); focarProximo(campo, idx, 1); }
    if (e.key === 'ArrowUp') { e.preventDefault(); focarProximo(campo, idx, -1); }
  };

  useEffect(() => {
    api.get('/unidades').then(r => setUnidades(r.data || [])).catch(() => {});
  }, []);

  // anteriores: linhas já editadas — ao recarregar (frete aplicado), mantém a
  // margem/fator/unidade digitados e recalcula o preço pelo custo novo.
  const carregar = (anteriores = null) => api.get(`/entradas-nf/${entradaId}/simulacao-precos`)
    .then(({ data }) => {
      setDados(data);
      setFrete({
        valor: data.entrada.frete_externo > 0 ? String(data.entrada.frete_externo.toFixed(2)).replace('.', ',') : '',
        rateio: data.entrada.frete_externo_rateio || 'valor',
      });
      setLinhas(data.itens.map(i => {
        const ant = anteriores?.find(a => a.item_id === i.item_id);
        if (ant) {
          const fatorAnt = num(ant.fator_conversao) || 1;
          const custo = fatorAnt !== (num(i.fator_conversao) || 1)
            ? +(i.custo_chegada * (num(i.fator_conversao) || 1) / fatorAnt).toFixed(4)
            : i.custo_chegada;
          return {
            ...i, custo_chegada: custo,
            margem: ant.margem, alterado: ant.alterado,
            preco: ant.margem !== '' ? calcPreco(data, custo, num(ant.margem), i.comissao_pct || 0) : ant.preco,
            fator_conversao: ant.fator_conversao, unidade_cadastro: ant.unidade_cadastro,
            fator_original: ant.fator_original, unidade_original: ant.unidade_original,
          };
        }
        const margemIni = i.margem_cadastro > 0 ? String(i.margem_cadastro) : '';
        const l = {
          ...i, margem: margemIni, preco: '', alterado: false,
          // originais pra comparar depois e saber se precisa gravar algo
          // mesmo sem preço/margem alterado (só fator ou só unidade)
          fator_original: i.fator_conversao, unidade_original: i.unidade_cadastro || '',
        };
        if (margemIni) l.preco = calcPreco(data, i.custo_chegada, num(margemIni), i.comissao_pct || 0);
        return l;
      }));
    })
    .catch(err => setErro(err.response?.data?.erro || 'Erro ao carregar a simulação.'));

  useEffect(() => { carregar(); }, [entradaId]); // eslint-disable-line

  const aplicarFrete = async () => {
    setErro(''); setAplicandoFrete(true);
    try {
      await api.put(`/entradas-nf/${entradaId}/frete-externo`, {
        valor: numReais(frete.valor),
        rateio: frete.rateio,
        pesos: frete.rateio === 'peso'
          ? linhas.map(l => ({ item_id: l.item_id, peso_unit_kg: l.peso_unit_kg === '' ? null : num(l.peso_unit_kg) }))
          : [],
      });
      await carregar(linhas);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao aplicar o frete.');
    } finally { setAplicandoFrete(false); }
  };
  const onPeso = (idx, v) => setLinhas(prev => prev.map((l, i) => (i === idx ? { ...l, peso_unit_kg: v } : l)));
  const totalFreteRateado = linhas.reduce((a, l) => a + (l.frete_externo_item || 0), 0);

  // Σ% dos índices aplicáveis (só no divisor entra na conta)
  const somaIdx = dados?.metodo === 'markup_divisor' ? (dados?.indices?.total_aplicavel || 0) : 0;

  function calcPreco(d, custo, margem, comissao = 0) {
    if (!(custo > 0)) return '';
    if (d.metodo === 'markup_comum') return (custo * (1 + margem / 100)).toFixed(2);
    const divisor = 1 - ((d.indices?.total_aplicavel || 0) + comissao + margem) / 100;
    if (divisor <= 0.0001) return '';
    return (custo / divisor).toFixed(2);
  }
  function calcMargem(d, custo, preco, comissao = 0) {
    if (!(custo > 0) || !(preco > 0)) return '';
    if (d.metodo === 'markup_comum') return ((preco / custo - 1) * 100).toFixed(2);
    return ((1 - custo / preco) * 100 - (d.indices?.total_aplicavel || 0) - comissao).toFixed(2);
  }

  const setLinha = (idx, patch) =>
    setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, ...patch, alterado: true } : l));

  const onMargem = (idx, v) => {
    const l = linhas[idx];
    setLinha(idx, { margem: v, preco: v === '' ? '' : calcPreco(dados, l.custo_chegada, num(v), l.comissao_pct || 0) });
  };
  const onPreco = (idx, v) => {
    const l = linhas[idx];
    setLinha(idx, { preco: v, margem: v === '' ? '' : calcMargem(dados, l.custo_chegada, num(v), l.comissao_pct || 0) });
  };

  // Corrige a qtde/embalagem (fator de conversão) direto aqui, mesmo com a
  // nota já confirmada — recalcula o custo de chegada na hora (custo ×
  // fator = valor faturado por unidade da NF, uma constante; muda só o
  // divisor) e, se já tem margem definida, o preço de venda também.
  const onFator = (idx, v) => {
    const l = linhas[idx];
    const fatorAntigo = num(l.fator_conversao) || 1;
    const fatorNovo = Math.max(0.0001, num(v) || 1);
    const novoCusto = +(l.custo_chegada * fatorAntigo / fatorNovo).toFixed(4);
    const novoPreco = l.margem !== '' ? calcPreco(dados, novoCusto, num(l.margem), l.comissao_pct || 0) : l.preco;
    setLinha(idx, { fator_conversao: v, custo_chegada: novoCusto, preco: novoPreco });
  };

  const onUnidade = (idx, v) => setLinha(idx, { unidade_cadastro: v.toUpperCase() });

  // Copia a margem do 1º item da lista pra todos os demais (com produto
  // vinculado), recalculando o preço de cada um pelo seu próprio custo de
  // chegada/comissão — útil quando a nota tem vários itens do mesmo produto
  // (ex.: tamanhos/variações) que devem levar a mesma margem.
  const aplicarMargemPrimeiro = () => {
    if (linhas.length < 2) return;
    const margemBase = linhas[0]?.margem;
    if (!margemBase || num(margemBase) <= 0) return;
    setLinhas(prev => prev.map((l, i) => {
      if (i === 0 || !l.produto_id) return l;
      const preco = calcPreco(dados, l.custo_chegada, num(margemBase), l.comissao_pct || 0);
      return { ...l, margem: margemBase, preco, alterado: true };
    }));
  };

  const ehPendente = dados?.entrada?.status !== 'confirmada';

  const fatorMudou = l => Math.abs(num(l.fator_conversao) - num(l.fator_original)) > 0.0001;
  const unidadeMudou = l => (l.unidade_cadastro || '') !== (l.unidade_original || '');

  const gravar = async () => {
    // Linha entra se tem preço/margem pra gravar OU só corrigiu fator/unidade
    // (sem necessariamente ter mexido no preço)
    const aGravar = linhas.filter(l => l.produto_id && l.alterado &&
      (num(l.preco) > 0 || fatorMudou(l) || unidadeMudou(l)));
    if (!aGravar.length) { setErro('Nenhum preço, qtde/embalagem ou unidade alterado para gravar.'); return; }

    const corrigeEstoque = !ehPendente && aGravar.some(fatorMudou);
    if (corrigeEstoque && !window.confirm(
      'Corrigir a qtde/embalagem de um item desta nota (já confirmada) também corrige o ESTOQUE já lançado ' +
      '(a própria entrada no kardex passa a mostrar a quantidade certa). Confirma?'
    )) return;

    setErro(''); setSalvando(true);
    try {
      const { data } = await api.post(`/entradas-nf/${entradaId}/aplicar-precos`, {
        atualizar_custo: atualizarCusto,
        itens: aGravar.map(l => ({
          item_id: l.item_id,
          produto_id: l.produto_id,
          preco_venda: num(l.preco) || undefined,
          margem_pct: l.margem === '' ? null : num(l.margem),
          custo: l.custo_chegada,
          fator_conversao: fatorMudou(l) ? l.fator_conversao : undefined,
          unidade: unidadeMudou(l) ? l.unidade_cadastro : undefined,
        })),
      });
      onGravado?.(data.atualizados, data.aplicado_agora);
      onClose();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao gravar os preços.');
    } finally { setSalvando(false); }
  };

  // Reconcilia o estoque desta nota (já confirmada) contra o fator de
  // conversão ATUAL do cadastro de cada produto — diferente de editar
  // "Qtde Prod." linha a linha: aqui corrige TODOS os itens de uma vez,
  // mesmo os que o operador nunca chegou a tocar (útil pra notas que já
  // tinham o fator certo no cadastro, mas foram confirmadas antes do
  // estoque considerar isso). Idempotente — item já certo não muda.
  const reconciliarEstoque = async () => {
    if (!window.confirm(
      'Corrige o estoque de TODOS os itens desta nota pra bater com a qtde faturada × fator de conversão ' +
      'atual do cadastro de cada produto (a própria entrada no kardex é ajustada, sem gerar um "ajuste" à parte). Confirma?'
    )) return;
    setErro(''); setReconciliando(true); setResultReconciliar(null);
    try {
      const { data } = await api.post(`/entradas-nf/${entradaId}/reconciliar-estoque`);
      setResultReconciliar(data.corrigidos || []);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao reconciliar o estoque.');
    } finally { setReconciliando(false); }
  };

  const ehDivisor = dados?.metodo === 'markup_divisor';

  return (
    <div className={s.overlay} style={{ zIndex: 5000 }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ width: 1750, maxWidth: 'calc(100vw - 24px)' }}>
        <div className={s.modalTitle}>
          💲 Simulação de Preços — NF {dados?.entrada?.numero_nf || ''} · {dados?.entrada?.fornecedor_nome || ''}
        </div>
        <div className={s.modalBody}>
          {erro && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4,
              padding: '6px 10px', fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>{erro}</div>
          )}
          {!dados ? <p>Carregando…</p> : (
            <>
              <p style={{ fontSize: 12, color: '#475569', margin: '0 0 8px' }}>
                Método: <strong>{ehDivisor ? 'Markup divisor (taxa de marcação)' : 'Markup comum (multiplicador)'}</strong>
                {' · '}Regime: <strong>{dados.regime}</strong>
                {' · '}Tipo de movimento: <strong>{dados.entrada.tmf_nome || '—'}</strong>
                {ehDivisor && (
                  <> · Σ índices: <strong style={{ color: '#b91c1c' }}>{fmt(somaIdx)}%</strong>
                    {' '}<span style={{ color: '#94a3b8' }}>
                      ({dados.eh_simples
                        ? `Simples ${fmt(dados.indices.simples)} + CustoOP ${fmt(dados.indices.custo_op)} + Outros ${fmt(dados.indices.outros)}`
                        : `ICMS ${fmt(dados.indices.icms)} + CSLL ${fmt(dados.indices.csll)} + IRPJ ${fmt(dados.indices.irpj)} + CustoOP ${fmt(dados.indices.custo_op)} + Outros ${fmt(dados.indices.outros)}`})
                    </span></>
                )}
              </p>
              {ehPendente && (
                <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 4,
                  padding: '5px 10px', fontSize: 11.5, color: '#92400e', marginBottom: 8 }}>
                  ⚠ Nota ainda pendente — os preços gravados aqui ficam presos nesta nota e só entram
                  de fato no cadastro dos produtos quando a nota for <strong>confirmada</strong>
                  (junto com a entrada de estoque).
                </div>
              )}
              {!ehPendente && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <button type="button" className={s.btnErp} disabled={reconciliando}
                    title="Corrige o estoque de todos os itens desta nota pra bater com qtde faturada × fator de conversão do cadastro — útil quando o fator já está certo no produto mas o estoque não foi corrigido junto"
                    onClick={reconciliarEstoque}>
                    {reconciliando ? 'Corrigindo estoque…' : '🔧 Corrigir estoque desta nota'}
                  </button>
                  {resultReconciliar && (
                    resultReconciliar.length === 0 ? (
                      <span style={{ fontSize: 11.5, color: '#166534' }}>✔ Estoque já estava certo em todos os itens.</span>
                    ) : (
                      <span style={{ fontSize: 11.5, color: '#166534' }}>
                        ✔ {resultReconciliar.length} item(ns) corrigido(s):{' '}
                        {resultReconciliar.map(c => `${c.codigo_interno} (${c.quantidade_antes}→${c.quantidade_depois})`).join(', ')}
                      </span>
                    )
                  )}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6,
                border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', background: '#f8fafc' }}>
                <strong style={{ fontSize: 12, color: '#1e3a5f' }}>🚚 Frete por fora (CT-e)</strong>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  R$
                  <input className={s.inputErp} inputMode="decimal" style={{ width: 110, textAlign: 'right' }}
                    placeholder="0,00" value={frete.valor}
                    onFocus={e => e.target.select()}
                    onChange={e => setFrete(f => ({ ...f, valor: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); aplicarFrete(); } }} />
                </label>
                <span style={{ fontSize: 12 }}>Ratear por:</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <input type="radio" name="rateioFrete" checked={frete.rateio === 'valor'}
                    onChange={() => setFrete(f => ({ ...f, rateio: 'valor' }))} />
                  Valor dos itens da NF-e
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <input type="radio" name="rateioFrete" checked={frete.rateio === 'peso'}
                    onChange={() => setFrete(f => ({ ...f, rateio: 'peso' }))} />
                  Peso
                </label>
                <button type="button" className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={aplicandoFrete}
                  title="Rateia o frete nos itens e soma no custo de chegada (o valor fica salvo nesta nota)"
                  onClick={aplicarFrete}>
                  {aplicandoFrete ? 'Aplicando…' : 'Aplicar frete'}
                </button>
                {dados.entrada.frete_externo > 0 && (
                  <span style={{ fontSize: 11.5, color: dados.entrada.frete_externo_sem_base ? '#b91c1c' : '#166534' }}>
                    {dados.entrada.frete_externo_sem_base
                      ? `⚠ R$ ${fmt(dados.entrada.frete_externo)} sem rateio — informe o peso dos itens na coluna "Peso unit."`
                      : `✔ R$ ${fmt(totalFreteRateado)} rateado por ${dados.entrada.frete_externo_rateio === 'peso' ? 'peso' : 'valor'} no custo de chegada`}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                <button type="button" className={s.btnErp}
                  disabled={linhas.length < 2 || !linhas[0]?.margem || num(linhas[0].margem) <= 0}
                  title="Copia a margem % do primeiro item da lista para todos os demais, recalculando o preço de cada um"
                  onClick={aplicarMargemPrimeiro}>
                  ⤵ Aplicar margem do 1º item nos demais
                </button>
              </div>
              <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid #e2e8f0' }}>
                <table className={s.modalTabela} style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}>Cód.</th>
                      <th style={{ minWidth: 380, maxWidth: 380 }}>Descrição Fornecedor (XML)</th>
                      <th style={{ minWidth: 340, maxWidth: 340 }}>Produto (cadastro)</th>
                      <th style={{ width: 64, textAlign: 'right' }}>Qtd</th>
                      <th style={{ width: 115, textAlign: 'right' }}
                        title="Quantidade por embalagem (fator de conversão) — ajustar aqui recalcula o custo e, se a nota já foi confirmada, corrige o estoque lançado">
                        Qtde Prod.
                      </th>
                      <th style={{ width: 72 }} title="Unidade que veio faturada na NF">Unid. NF</th>
                      <th style={{ width: 110 }} title="Unidade do cadastro do produto — editável pra corrigir">Unid. Cadastro</th>
                      {frete.rateio === 'peso' && (
                        <th style={{ width: 100, textAlign: 'right' }}
                          title="Peso por unidade do cadastro (kg) usado no rateio do frete — vem do cadastro do produto e pode ser corrigido aqui só para esta nota">
                          Peso unit. (kg)
                        </th>
                      )}
                      <th style={{ width: 120, textAlign: 'right' }}>Custo Chegada</th>
                      <th style={{ width: 105, textAlign: 'right' }}>Preço Atual</th>
                      <th style={{ width: 72, textAlign: 'right' }} title='Comissão do item (produto ou índice do tipo de movimento) — entra na conta do divisor'>Com.%</th>
                      <th style={{ width: 115 }}>Margem %</th>
                      <th style={{ width: 130 }}>Preço Venda</th>
                      {ehDivisor && <th style={{ width: 40 }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, idx) => (
                      <tr key={l.item_id} style={!l.produto_id ? { opacity: 0.5 } : undefined}
                        title={!l.produto_id ? 'Item sem produto vinculado no cadastro — não pode ser atualizado' : ''}>
                        <td>{l.codigo_interno || '—'}</td>
                        <td title={l.descricao_xml}
                          style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 380 }}>
                          {l.descricao_xml}
                        </td>
                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 340 }}
                          title={l.produto_nome || ''}>
                          {l.produto_nome || <span style={{ color: '#b71c1c', fontStyle: 'italic' }}>Não localizado</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmt(l.quantidade)}</td>
                        <td>
                          <input type="number" step="0.0001" min="0.0001" className={s.inputErp}
                            style={{ width: '100%', minWidth: 100, textAlign: 'right', fontSize: 12.5,
                              background: fatorMudou(l) ? '#fff7ed' : undefined }}
                            disabled={!l.produto_id}
                            value={l.fator_conversao}
                            onChange={e => onFator(idx, e.target.value)} />
                        </td>
                        <td style={{ textAlign: 'center', color: '#64748b' }}>{l.unidade_xml || '—'}</td>
                        <td>
                          <select className={s.selectErp}
                            style={{ width: '100%', minWidth: 100,
                              background: unidadeMudou(l) ? '#fff7ed' : undefined }}
                            disabled={!l.produto_id}
                            value={l.unidade_cadastro || ''}
                            onChange={e => onUnidade(idx, e.target.value)}>
                            {!unidades.some(u => u.codigo === l.unidade_cadastro) && l.unidade_cadastro && (
                              <option value={l.unidade_cadastro}>{l.unidade_cadastro} (fora do cadastro)</option>
                            )}
                            {!l.unidade_cadastro && <option value="">— Selecione —</option>}
                            {unidades.map(u => (
                              <option key={u.id} value={u.codigo}>{u.codigo}{u.descricao ? ` — ${u.descricao}` : ''}</option>
                            ))}
                          </select>
                        </td>
                        {frete.rateio === 'peso' && (
                          <td>
                            <input type="number" step="0.001" min="0" className={s.inputErp}
                              style={{ width: '100%', minWidth: 80, textAlign: 'right', fontSize: 12.5 }}
                              value={l.peso_unit_kg ?? ''}
                              onFocus={e => e.target.select()}
                              onChange={e => onPeso(idx, e.target.value)} />
                          </td>
                        )}
                        <td style={{ textAlign: 'right', fontWeight: 600 }}
                          title={[
                            l.fator_conversao > 1 ? `Já dividido pela qtde/embalagem (${l.fator_conversao}×) — custo por unidade de estoque` : '',
                            l.frete_externo_unit > 0 ? `Inclui R$ ${fmt4(l.frete_externo_unit)}/un de frete por fora (CT-e)` : '',
                          ].filter(Boolean).join(' · ')}>
                          {fmt4(l.custo_chegada)}{l.fator_conversao > 1 && ' 📦'}{l.frete_externo_unit > 0 && ' 🚚'}
                        </td>
                        <td style={{ textAlign: 'right', color: '#64748b' }}>
                          {l.preco_atual != null ? fmt(l.preco_atual) : '—'}
                        </td>
                        <td style={{ textAlign: 'right', color: '#7c3aed' }}>{fmt(l.comissao_pct || 0)}</td>
                        <td>
                          <input type="number" step="0.01" min="0" className={s.inputErp}
                            style={{ width: '100%', minWidth: 100, fontSize: 12.5 }} disabled={!l.produto_id}
                            ref={el => { campoRefs.current['margem-' + idx] = el; }}
                            onKeyDown={navegacao('margem', idx)}
                            value={l.margem}
                            onChange={e => onMargem(idx, e.target.value)} />
                        </td>
                        <td>
                          <input type="number" step="0.01" min="0" className={s.inputErp}
                            style={{ width: '100%', minWidth: 115, fontSize: 12.5, fontWeight: 700,
                              background: l.alterado && num(l.preco) > 0 ? '#f0fdf4' : undefined }}
                            disabled={!l.produto_id}
                            ref={el => { campoRefs.current['preco-' + idx] = el; }}
                            onKeyDown={navegacao('preco', idx)}
                            value={l.preco}
                            onChange={e => onPreco(idx, e.target.value)} />
                        </td>
                        {ehDivisor && (
                          <td style={{ textAlign: 'center' }}>
                            <button type="button" className={s.btnErp}
                              style={{ height: 24, padding: '0 6px' }} disabled={!l.produto_id}
                              title="Formação de preço detalhada deste item"
                              onClick={() => setDetalheIdx(idx)}>🧮</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className={s.modalFooter} style={{ justifyContent: 'space-between' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={atualizarCusto}
              onChange={e => setAtualizarCusto(e.target.checked)} />
            Atualizar também o preço de custo do cadastro com o custo de chegada
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={gravar} disabled={salvando || !dados}
              title={ehPendente ? 'Grava os preços NA NOTA — só entram no cadastro quando ela for confirmada' : 'Atualiza o preço de venda no cadastro dos produtos agora'}>
              {salvando ? 'Gravando…' : ehPendente
                ? `Gravar preços na nota (${linhas.filter(l => l.alterado && num(l.preco) > 0).length})`
                : `Gravar preços (${linhas.filter(l => l.alterado && num(l.preco) > 0).length})`}
            </button>
            <button className={s.btnErp} onClick={onClose} disabled={salvando}>Fechar</button>
          </div>
        </div>
      </div>

      {/* ── Formação de Preço detalhada (markup divisor) — estilo do print ── */}
      {detalheIdx !== null && dados && (
        <FormacaoPrecoItem
          dados={dados}
          linha={linhas[detalheIdx]}
          onAplicar={(preco, margem) => {
            setLinha(detalheIdx, { preco: preco.toFixed(2), margem: margem.toFixed(2) });
            setDetalheIdx(null);
          }}
          onClose={() => setDetalheIdx(null)}
        />
      )}
    </div>
  );
}

/* Janela de formação de preço de UM item (espelho do sistema antigo):
   CMA (custos de chegada abertos) → índices do TMF (CC/CO) → margem → preço. */
function FormacaoPrecoItem({ dados, linha, onAplicar, onClose }) {
  const [margem, setMargem] = useState(linha.margem !== '' ? linha.margem
    : linha.margem_cadastro > 0 ? String(linha.margem_cadastro) : '10');
  const [verComposicao, setVerComposicao] = useState(false);

  const custo = linha.custo_chegada;
  const idx   = dados.indices;
  const comissao = linha.comissao_pct || 0;
  const somaIdx = (idx.total_aplicavel || 0) + comissao;
  const m = parseFloat(String(margem).replace(',', '.')) || 0;
  const totalPct = somaIdx + m;
  const divisor  = 1 - totalPct / 100;
  const preco    = divisor > 0.0001 && custo > 0 ? custo / divisor : 0;
  const coefGerado = divisor > 0 ? divisor * 100 : 0;
  const indiceMarcacao = custo > 0 && preco > 0 ? preco / custo : 0;

  const lin = { display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 0' };
  // R$ de cada componente calculado SOBRE O PREÇO (percentuais por dentro)
  const rs = p => preco > 0 ? ' (R$ ' + fmt(preco * p / 100) + ')' : '';

  return (
    <div className={s.overlay} style={{ zIndex: 6000 }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ width: 460 }}>
        <div className={s.modalTitle}>🧮 Formação de Preço — {linha.descricao}</div>
        <div className={s.modalBody}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#1e3a5f', margin: '0 0 4px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Custo da Mercadoria Adquirida (por unidade)</span>
            <button type="button" className={s.btnErp}
              style={{ height: 20, padding: '0 6px', fontSize: 11 }}
              title="Ver tudo que compõe este custo (inclusive itens zerados nesta nota)"
              onClick={() => setVerComposicao(true)}>🔎 Composição</button>
          </div>
          {linha.fator_conversao > 1 && (
            <div style={{ fontSize: 11, color: '#92400e', marginBottom: 4 }}>
              📦 Produto com embalagem (qtde/embalagem: {linha.fator_conversao}×) — valores já
              divididos, é o custo por unidade de estoque/venda, não por unidade faturada na NF.
            </div>
          )}
          <div style={lin}><span>R$ Unitário NF</span><strong>{fmt4(linha.valor_unitario_nf)}</strong></div>
          <div style={{ ...lin, borderTop: '1px solid #e2e8f0', fontWeight: 700 }}>
            <span>= R$ Custo de Chegada</span><span>{fmt4(custo)}</span>
          </div>

          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#1e3a5f', margin: '10px 0 4px' }}>
            Percentuais sobre a venda ({dados.entrada.tmf_nome || 'tipo de movimento da nota'})
          </div>
          {dados.eh_simples
            ? <div style={lin}><span>Simples Nacional %</span><span>{fmt(idx.simples)}{rs(idx.simples)}</span></div>
            : <>
                <div style={lin}><span>ICMS D/E %</span><span>{fmt(idx.icms)}{rs(idx.icms)}</span></div>
                <div style={lin}><span>C.S.L.L %</span><span>{fmt(idx.csll)}{rs(idx.csll)}</span></div>
                <div style={lin}><span>I.R.P.J %</span><span>{fmt(idx.irpj)}{rs(idx.irpj)}</span></div>
              </>}
          <div style={lin}><span>Custo Operacional %</span><span>{fmt(idx.custo_op)}{rs(idx.custo_op)}</span></div>
          <div style={lin}><span>Outros %</span><span>{fmt(idx.outros)}{rs(idx.outros)}</span></div>
          <div style={lin}>
            <span>Comissão % {dados.comissao_base === 'produto' && linha.comissao_pct !== (idx.comissao || 0)
              ? '(do produto)' : '(do tipo de movimento)'}</span>
            <span>{fmt(comissao)}{rs(comissao)}</span>
          </div>
          <div style={{ ...lin, alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: '#166534' }}>Margem de Lucro %</span>
            <input type="number" step="0.01" min="0" className={s.inputErp}
              style={{ width: 90, borderColor: '#16a34a', background: '#f0fdf4', fontWeight: 700 }}
              value={margem} onChange={e => setMargem(e.target.value)} autoFocus />
          </div>
          <div style={{ ...lin, borderTop: '1px solid #e2e8f0', fontWeight: 700, color: '#b91c1c' }}>
            <span>Total CC + CO + ML %</span><span>{fmt(totalPct)}{preco > 0 ? ' (R$ ' + fmt(preco - custo) + ')' : ''}</span>
          </div>

          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6,
            padding: '8px 10px', marginTop: 10 }}>
            <div style={{ ...lin, color: '#166534', fontWeight: 700 }}><span>R$ Margem de Lucro ({fmt(m)}% do preço)</span><span>{preco > 0 ? fmt(preco * m / 100) : '—'}</span></div>
            <div style={lin}><span>Coeficiente Gerado %</span><span>{fmt(coefGerado)}</span></div>
            <div style={lin}><span>Índice Marcação (markup equivalente)</span><span>{indiceMarcacao ? indiceMarcacao.toFixed(6) : '—'}</span></div>
            <div style={{ ...lin, fontSize: 15, fontWeight: 800 }}>
              <span>R$ Preço de Venda</span>
              <span style={{ color: divisor > 0.0001 ? '#15803d' : '#b91c1c' }}>
                {divisor > 0.0001 ? fmt(preco) : 'Σ% ≥ 100 — impossível'}
              </span>
            </div>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
            disabled={!(preco > 0)}
            onClick={() => onAplicar(preco, m)}>
            Aplicar na grade
          </button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>

      {verComposicao && (
        <div className={s.overlay} style={{ zIndex: 7000 }}
          onMouseDown={e => e.target === e.currentTarget && setVerComposicao(false)}>
          <div className={s.modal} style={{ width: 400 }}>
            <div className={s.modalTitle}>🔎 Composição do Custo de Chegada</div>
            <div className={s.modalBody}>
              <div style={lin}><span>R$ Unitário NF</span><strong>{fmt4(linha.valor_unitario_nf)}</strong></div>
              <div style={lin}><span>+ IPI (rateado por unidade)</span><span>{fmt4(linha.ipi_unit)}</span></div>
              <div style={lin}><span>+ ICMS-ST (rateado por unidade)</span><span>{fmt4(linha.icms_st_unit)}</span></div>
              <div style={lin}><span>± Frete + Seguro − Desconto da NOTA (rateio por valor do item)</span><span>{fmt4(linha.rateio_unit)}</span></div>
              <div style={lin}><span>+ Frete por fora (CT-e){dados.entrada.frete_externo_rateio === 'peso' ? ' — rateio por peso' : ' — rateio por valor do item'}</span><span>{fmt4(linha.frete_externo_unit)}</span></div>
              <div style={lin}><span>− Desconto do ITEM</span><span>{linha.desconto_unit > 0 ? '−' : ''}{fmt4(linha.desconto_unit)}</span></div>
              <div style={{ ...lin, borderTop: '1px solid #e2e8f0', fontWeight: 700 }}>
                <span>= R$ Custo de Chegada</span><span>{fmt4(custo)}</span>
              </div>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, lineHeight: 1.5 }}>
                Todos os valores acima vêm do XML da nota (rateados por unidade quando são do
                total da nota, não do item). Nesta nota, alguns componentes deram R$ 0,00 — não é
                que foram ignorados, é que a nota não trouxe esse valor para este item.
                <br /><br />
                <strong>Não entram</strong> no custo de chegada: PIS/COFINS (já estão embutidos no
                valor cobrado pelo fornecedor, não é um valor à parte somado por fora) e ICMS
                normal — sem crédito a recuperar no Simples Nacional.
              </p>
            </div>
            <div className={s.modalFooter}>
              <button className={s.btnErp} onClick={() => setVerComposicao(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
